import { observer } from "mobx-react";
import BlockTitle from "../_BlockTitle";
import styles from "./styles/ShopTerrace.module.scss";
import ViewMoreButton from "../ViewMoreButton";
import {
  gearLaunchSupportEmail,
  useGearLaunchShop,
} from "../../../../hooks/useGearLaunchShop";
import EventListItemLoading from "../EventsBlock/EventListItemLoading";
import classNames from "classnames";
import FontAwesome from "../../../utilities/FontAwesome";
import MailToLink from "../../../misc/MailToLink";
import ClickableLink from "../../../utilities/ClickableLink";
import ShopOfferings from "./ShopOfferings";
import { useStores } from "../../../../stores/useStores";

const LoadingItem = (props: { className?: string }) => {
  const loadingItemClassName = classNames({
    [styles.loadingItem]: true,
    [props.className || ""]: props.className,
  });

  return (
    <div className={loadingItemClassName}>
      <div>
        <FontAwesome prefix={"fas"} name="fa-image" />
      </div>
      <EventListItemLoading />
    </div>
  );
};

export const GearLaunchContactInfo = () => {
  const { organizationStore, userStore } = useStores();

  const subject = `Store update request from '${organizationStore.currentOrganization.title}' on '${organizationStore.organization.title}'`;

  const body = `My page ID is ${organizationStore.currentOrganization.id}, can you please update the following content in my store:    
    
    
Thanks,
${userStore.firstName} ${userStore.lastName}`;

  return (
    <div className={styles.shopInfoMessage}>
      <span>
        To update the name, image, or&nbsp;
        <ClickableLink
          href={
            "https://cdn.cleversite.com/media/media/schoolblocks_catalogue.pdf"
          }
        >
          items for sale
        </ClickableLink>
        , contact GearLaunch at&nbsp;
      </span>
      <MailToLink
        recipient={gearLaunchSupportEmail}
        subject={subject}
        body={body}
      >
        {gearLaunchSupportEmail}
      </MailToLink>
      .
    </div>
  );
};

export const LoadingItemArray = (props: {
  numItems: number;
  className?: string;
}) => {
  return (
    <>
      {Array(props.numItems)
        .fill(null)
        .map((_, index) => (
          <LoadingItem className={props.className} key={index} />
        ))}
    </>
  );
};

export const LoadingMessage = (props: {
  className?: string;
  numItems: number;
}) => {
  const { organizationStore, userStore } = useStores();

  return (
    <>
      <div className={styles.shopInfoMessage}>
        {userStore.isEditor ? (
          <>
            <span
              style={{ fontWeight: "bold" }}
            >{`Your store is being created! All products will be branded with 
                    '${organizationStore.currentOrganization.title}'.`}</span>
            <GearLaunchContactInfo />
            <span>{"Please refresh the page to see your custom items."}</span>
          </>
        ) : (
          <span
            style={{ fontWeight: "bold" }}
          >{`The store for '${organizationStore.currentOrganization.title}' is being created.
                    Please refresh the page to see custom merchandise.`}</span>
        )}
      </div>
      <div className={props.className || styles.container}>
        <LoadingItemArray numItems={props.numItems} />
      </div>
    </>
  );
};

const ShopTerrace = observer(
  (props: { blockObj: IShopTerrace; aboveTheFold: boolean }) => {
    const { userStore } = useStores();
    const { data, isLoading } = useGearLaunchShop();

    const viewMoreButtonText = (
      <span>
        {"Visit Store "}
        <FontAwesome
          name={"fa-external-link-alt"}
          prefix={"fas"}
          aria-label={"External Link"}
          title={"External Link"}
        />
      </span>
    );

    return (
      <article>
        {props.blockObj?.status_code === 202 ||
        data?.status === "IN_PROGRESS" ? (
          <>
            {userStore.isEditor ? (
              <>
                <BlockTitle
                  blockObj={props.blockObj}
                  aboveTheFold={props.aboveTheFold}
                  blockTitleContainerClassName={styles.blockTitleContainer}
                  showViewMore={!!data?.storeUrl}
                  viewMoreButtonText={viewMoreButtonText}
                  viewMoreButtonUrl={data?.storeUrl}
                />
                <LoadingMessage numItems={4} />
              </>
            ) : null}
          </>
        ) : (
          <>
            <BlockTitle
              blockObj={props.blockObj}
              aboveTheFold={props.aboveTheFold}
              blockTitleContainerClassName={styles.blockTitleContainer}
              showViewMore={!!data?.storeUrl}
              viewMoreButtonText={viewMoreButtonText}
              viewMoreButtonUrl={data?.storeUrl}
            />
            <ShopOfferings
              data={data?.items}
              numItemsLoading={4}
              className={styles.container}
              loadingClassName={styles.loadingClassName}
              isLoading={isLoading}
            />
            {userStore.isEditor && <GearLaunchContactInfo />}
          </>
        )}
        {!!data?.storeUrl && (
          <div className={styles.blockTitleContainerMobile}>
            <ViewMoreButton
              terraceType={props.blockObj.type}
              text={viewMoreButtonText}
              url={data.storeUrl}
            />
          </div>
        )}
      </article>
    );
  },
);

export default ShopTerrace;
